import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

export interface CursorPosition {
  x: number
  y: number
}

interface AnimatedCursorProps {
  /** Centre of the button being clicked, relative to the positioned parent. */
  position: CursorPosition | null
  /** The click has landed — the pointer dips and a ripple leaves the button. */
  pressed: boolean
  /** How long the move to `position` takes. */
  travelMs: number
}

/** Oversized on purpose: the stage is scaled down, and a true-size pointer disappears. */
const CURSOR_SIZE = 48
/** The arrow's point within the 24-unit viewBox, so the tip — not the corner — lands on target. */
const TIP_X = (5 / 24) * CURSOR_SIZE
const TIP_Y = (2.5 / 24) * CURSOR_SIZE

/** Where the pointer flies in from, relative to its target. */
const ENTRY_OFFSET = 'translate(72px, 84px)'

/**
 * The pointer that performs the Add to cart click. Purely illustrative — it flies to
 * whatever the card currently renders as its cart CTA, so it keeps landing on the
 * button even when the card's own layout shifts underneath it.
 */
export function AnimatedCursor({ position, pressed, travelMs }: AnimatedCursorProps) {
  const [arrived, setArrived] = useState(false)

  // A transition needs a painted starting frame, so the travel begins one frame after the
  // target is known. Once arrived it stays put — the button moving is not a new flight.
  useEffect(() => {
    if (!position || arrived) return
    const frame = requestAnimationFrame(() => setArrived(true))
    return () => cancelAnimationFrame(frame)
  }, [position, arrived])

  if (!position) return null

  return (
    <div
      className="pointer-events-none absolute z-20 transition-[left,top] ease-in-out"
      style={{
        left: position.x - TIP_X,
        top: position.y - TIP_Y,
        transitionDuration: `${travelMs}ms`,
      }}
      aria-hidden="true"
    >
      <div
        className="transition-[transform,opacity] duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ transform: arrived ? 'translate(0, 0)' : ENTRY_OFFSET, opacity: arrived ? 1 : 0 }}
      >
        {/* Springs from the arrow's tip, which is the point resting on the button. */}
        <span
          className={cn(
            'absolute block size-3 rounded-full transition-transform duration-200 ease-out',
            pressed ? 'quote-cursor-ripple' : 'scale-0'
          )}
          style={{ left: TIP_X - 6, top: TIP_Y - 6 }}
        />
        <svg
          viewBox="0 0 24 24"
          style={{ width: CURSOR_SIZE, height: CURSOR_SIZE }}
          className={cn(
            'relative block drop-shadow-[0_3px_8px_rgba(0,0,0,0.4)] transition-transform duration-150 ease-out',
            pressed && 'scale-90'
          )}
        >
          <path
            d="M5 2.5 19 11.5 12.6 12.9 16.2 19.8 13.6 21.1 10 14.2 5 18.6Z"
            fill="white"
            stroke="#111827"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <style>
        {`
          .quote-cursor-ripple {
            animation: quote-cursor-ripple 600ms ease-out forwards;
            background: color-mix(in oklab, var(--brand-700) 55%, transparent);
          }
          @keyframes quote-cursor-ripple {
            from { transform: scale(0); opacity: 0.85; }
            to { transform: scale(10); opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) {
            .quote-cursor-ripple { animation: none; opacity: 0; }
          }
        `}
      </style>
    </div>
  )
}
